import React, { useState } from 'react';
import {
    Table, Card, Input, Select, Button, Space, Tag, Typography,
    Modal, Form, Upload, Progress, Drawer, Descriptions, Tooltip,
    Popconfirm, message
} from 'antd';
import {
    SearchOutlined, ReloadOutlined, UploadOutlined, CloudUploadOutlined,
    EditOutlined
} from '@ant-design/icons';
import CompileRoleSwitcher, { getCompileRole, setCompileRole, MY_ENTERPRISE, MOCK_ENTERPRISES } from '../CompileRole';

const { Title, Text } = Typography;
const { TextArea } = Input;

// TODO: 联调时替换为接口数据
// 编译链仅做文件上传与保存，不识别 GCC版本/目标架构/C库等元信息
const MOCK_TOOLCHAINS = [
    {
        id: 'tc_001',
        name: '海思Hi3516DV300编译链',
        remark: '带NEON优化，适用于DV300量产固件，关闭了低功耗模式',
        address: 'https://oss.hmy.com/toolchain/tc_001_hisi3516dv300.tar.gz',
        fileSize: '759 MB',
        status: 'available',
        uploadTime: '2026-04-15 10:30:00',
        enterprise: '东莞康林物联网有限公司',
    },
    {
        id: 'tc_002',
        name: '全志T113编译链',
        remark: 'glibc版本，支持A53/A55多核，用于T113方案量产',
        address: 'https://oss.hmy.com/toolchain/tc_002_t113.tar.gz',
        fileSize: '1.1 GB',
        status: 'available',
        uploadTime: '2026-04-18 14:20:00',
        enterprise: '深圳智联科技有限公司',
    },
    {
        id: 'tc_003',
        name: '瑞芯微RV1106编译链',
        remark: '大文件上传中，完成后自动保存',
        address: '-',
        fileSize: '520 MB',
        status: 'uploading',
        uploadTime: '2026-04-20 09:15:00',
        enterprise: '东莞康林物联网有限公司',
    },
    {
        id: 'tc_004',
        name: '旧版Hi3518编译链',
        remark: '仅用于老产品维护，不建议新项目使用',
        address: 'https://oss.hmy.com/toolchain/tc_004_hi3518.tar.gz',
        fileSize: '380 MB',
        status: 'available',
        uploadTime: '2026-03-01 08:00:00',
        enterprise: '东莞康林物联网有限公司',
    },
    {
        id: 'tc_005',
        name: '地平线X3编译链',
        remark: '上传中断，文件不完整',
        address: '-',
        fileSize: '860 MB',
        status: 'uploadFailed',
        errorMsg: '网络中断导致上传失败，文件不完整，请重新上传',
        uploadTime: '2026-04-20 11:45:00',
        enterprise: '东莞康林物联网有限公司',
    },
];

const STATUS_MAP = {
    uploading: { text: '上传中', color: 'processing' },
    uploadFailed: { text: '上传失败', color: 'error' },
    available: { text: '可用', color: 'success' },
};

// MOCK: 编译链关联的编译任务
const MOCK_RELATED_TASKS = [
    { id: 'task_001', sdkServiceName: '单目', tag: 'v3.2.1', product: '普通单目云台摄像机', status: 'success', duration: '3分12秒', createTime: '2026-04-19 14:00:00' },
    { id: 'task_002', sdkServiceName: '双目', tag: 'v3.2.0', product: '普通单目云台摄像机', status: 'failed', duration: '1分05秒', createTime: '2026-04-17 10:30:00' },
    { id: 'task_003', sdkServiceName: '标准NVR', tag: 'v3.1.8', product: 'N2普通云台摄像机', status: 'success', duration: '4分30秒', createTime: '2026-04-10 09:00:00' },
];

const TASK_STATUS_MAP = {
    queued: { text: '排队中', color: 'default' },
    compiling: { text: '编译中', color: 'processing' },
    success: { text: '已完成', color: 'success' },
    failed: { text: '失败', color: 'error' },
    cancelled: { text: '已取消', color: 'default' },
};

// ══════════════════════════════════════════════
//  子组件：编辑编译链弹窗
// ══════════════════════════════════════════════

const EditModal = ({ open, record, onClose }) => {
    const [form] = Form.useForm();

    React.useEffect(() => {
        if (open && record) {
            form.setFieldsValue({ name: record.name, remark: record.remark });
        }
    }, [open, record, form]);

    const handleOk = () => {
        form.validateFields().then(() => {
            message.success('编译链信息已更新');
            form.resetFields();
            onClose();
        });
    };

    return (
        <Modal title="编辑编译链" open={open} onCancel={() => { form.resetFields(); onClose(); }} onOk={handleOk} okText="保存" width={480} destroyOnClose>
            <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                <Form.Item label="编译链名称" name="name" rules={[{ required: true, message: '请输入编译链名称' }]}>
                    <Input placeholder="如：海思Hi3516DV300编译链" maxLength={50} />
                </Form.Item>
                <Form.Item label="备注" name="remark">
                    <TextArea rows={3} placeholder="描述编译链的用途、差异点等" maxLength={200} showCount />
                </Form.Item>
            </Form>
        </Modal>
    );
};

// ══════════════════════════════════════════════
//  子组件：上传编译链弹窗
// ══════════════════════════════════════════════

const UploadModal = ({ open, onClose, isPlatform, record }) => {
    const [form] = Form.useForm();
    const [uploading, setUploading] = useState(false);
    const [percent, setPercent] = useState(0);

    // 再次上传：回显名称、备注与所属企业（归属企业不可变更），需重新选择文件
    React.useEffect(() => {
        if (open && record) {
            form.setFieldsValue({ enterprise: record.enterprise, name: record.name, remark: record.remark });
        }
    }, [open, record, form]);

    const handleOk = () => {
        form.validateFields().then(() => {
            // TODO: 调用上传接口
            setUploading(true);
            // 模拟上传进度
            let p = 0;
            const timer = setInterval(() => {
                p += Math.random() * 20;
                if (p >= 100) {
                    p = 100;
                    clearInterval(timer);
                    setUploading(false);
                    setPercent(0);
                    message.success('编译链上传成功，正在验证中');
                    form.resetFields();
                    onClose();
                }
                setPercent(Math.min(Math.round(p), 100));
            }, 500);
        });
    };

    return (
        <Modal
            title={record ? '再次上传编译链' : '上传编译链'}
            open={open}
            onCancel={() => { if (!uploading) { form.resetFields(); onClose(); } }}
            onOk={handleOk}
            okText="提交"
            confirmLoading={uploading}
            maskClosable={!uploading}
            width={560}
        >
            <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                {isPlatform && (
                    <Form.Item label="所属企业" name="enterprise" rules={[{ required: true, message: '请选择所属企业' }]}>
                        <Select
                            placeholder="请选择企业（代企业上传）"
                            disabled={!!record}
                            showSearch
                            optionFilterProp="label"
                            options={MOCK_ENTERPRISES.map(e => ({ value: e, label: e }))}
                        />
                    </Form.Item>
                )}
                <Form.Item label="编译链名称" name="name" rules={[{ required: true, message: '请输入编译链名称' }]}>
                    <Input placeholder="如：海思Hi3516DV300编译链" maxLength={50} />
                </Form.Item>
                <Form.Item label="备注" name="remark">
                    <TextArea rows={3} placeholder="描述编译链的用途、差异点等，方便后续区分" maxLength={200} showCount />
                </Form.Item>
                <Form.Item label="上传文件" name="file" rules={[{ required: true, message: '请选择编译链压缩包' }]}>
                    <Upload.Dragger
                        accept=".tar.gz,.tar.xz,.zip"
                        maxCount={1}
                        beforeUpload={() => false}
                    >
                        <p style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }}><CloudUploadOutlined /></p>
                        <p>点击或拖拽上传编译链压缩包</p>
                        <p style={{ color: '#999', fontSize: 12 }}>支持 .tar.gz / .tar.xz / .zip，最大 2GB</p>
                    </Upload.Dragger>
                </Form.Item>
                {uploading && <Progress percent={percent} status="active" />}
            </Form>
        </Modal>
    );
};

// ══════════════════════════════════════════════
//  子组件：编译链详情抽屉
// ══════════════════════════════════════════════

const DetailDrawer = ({ open, record, onClose }) => {
    if (!record) return null;

    const taskColumns = [
        { title: 'SDK服务', dataIndex: 'sdkServiceName', width: 100 },
        { title: 'SDK版本（TAG）', dataIndex: 'tag', width: 120 },
        { title: '目标产品', dataIndex: 'product' },
        {
            title: '状态', dataIndex: 'status', width: 80,
            render: (s) => <Tag color={TASK_STATUS_MAP[s]?.color}>{TASK_STATUS_MAP[s]?.text}</Tag>
        },
        { title: '耗时', dataIndex: 'duration', width: 100 },
        { title: '创建时间', dataIndex: 'createTime', width: 170 },
    ];

    return (
        <Drawer
            title="编译链详情"
            open={open}
            onClose={onClose}
            width={640}
        >
            <div style={{ marginBottom: 24 }}>
                <Space>
                    <Title level={5} style={{ margin: 0 }}>{record.name}</Title>
                    <Tag color={STATUS_MAP[record.status]?.color}>{STATUS_MAP[record.status]?.text}</Tag>
                </Space>
                {record.remark && (
                    <div style={{ marginTop: 8, color: '#666', fontSize: 13 }}>{record.remark}</div>
                )}
            </div>

            {record.status === 'uploadFailed' && (
                <div style={{ background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                    <Text type="danger">上传失败原因：{record.errorMsg}</Text>
                </div>
            )}

            <Descriptions bordered column={2} size="small" style={{ marginBottom: 24 }}>
                <Descriptions.Item label="地址" span={2}>
                    {record.address && record.address !== '-'
                        ? <Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>{record.address}</Text>
                        : <span style={{ color: '#ccc' }}>-</span>}
                </Descriptions.Item>
                <Descriptions.Item label="文件大小">{record.fileSize}</Descriptions.Item>
                <Descriptions.Item label="上传时间">{record.uploadTime}</Descriptions.Item>
            </Descriptions>

            <Title level={5}>关联编译任务（最近10次）</Title>
            <Table
                dataSource={MOCK_RELATED_TASKS}
                columns={taskColumns}
                rowKey="id"
                size="small"
                pagination={false}
            />
        </Drawer>
    );
};

// ══════════════════════════════════════════════
//  主组件：编译链管理
// ══════════════════════════════════════════════

const ToolchainManage = () => {
    const [role, setRole] = useState(getCompileRole());
    const isPlatform = role === 'platform';

    const [uploadOpen, setUploadOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [searchName, setSearchName] = useState('');
    const [searchType, setSearchType] = useState('name');
    const [searchStatus, setSearchStatus] = useState(undefined);
    const [searchEnterprise, setSearchEnterprise] = useState(undefined);

    const handleRoleChange = (next) => {
        setCompileRole(next); // 模块内共用，切换后其它页面保持一致
        setRole(next);
    };

    const handleDelete = (record) => {
        message.success(`已删除编译链：${record.name}`);
    };

    // 角色数据范围：企业角色仅本企业；平台角色可见全部，可按企业筛选
    const dataSource = MOCK_TOOLCHAINS.filter((tc) => {
        if (!isPlatform && tc.enterprise !== MY_ENTERPRISE) return false;
        if (isPlatform && searchEnterprise && tc.enterprise !== searchEnterprise) return false;
        if (searchStatus && tc.status !== searchStatus) return false;
        if (searchName) {
            const field = searchType === 'id' ? tc.id : tc.name;
            if (!String(field).toLowerCase().includes(searchName.toLowerCase())) return false;
        }
        return true;
    });

    const columns = [
        {
            title: '编译链名称',
            dataIndex: 'name',
            width: 200,
            render: (text, record) => (
                <div>
                    <Button type="link" style={{ padding: 0 }} onClick={() => { setCurrentRecord(record); setDetailOpen(true); }}>
                        {text}
                    </Button>
                    <div><Text copyable style={{ fontFamily: 'monospace', fontSize: 12, color: '#999' }}>{record.id}</Text></div>
                </div>
            ),
        },
        ...(isPlatform ? [{
            title: '所属企业',
            dataIndex: 'enterprise',
            width: 180,
        }] : []),
        {
            title: '备注',
            dataIndex: 'remark',
            width: 200,
            ellipsis: true,
            render: (text) => text ? (
                <Tooltip title={text}><span>{text}</span></Tooltip>
            ) : <span style={{ color: '#ccc' }}>-</span>,
        },
        {
            title: '地址',
            dataIndex: 'address',
            width: 320,
            render: (text) => text && text !== '-' ? (
                <Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>{text}</Text>
            ) : <span style={{ color: '#ccc' }}>-</span>,
        },
        { title: '文件大小', dataIndex: 'fileSize', width: 100 },
        {
            title: '状态',
            dataIndex: 'status',
            width: 90,
            render: (status, record) => (
                <Tooltip title={status === 'uploadFailed' ? record.errorMsg : undefined}>
                    <Tag color={STATUS_MAP[status]?.color} style={{ border: 'none' }}>
                        {STATUS_MAP[status]?.text}
                    </Tag>
                </Tooltip>
            ),
        },
        { title: '上传时间', dataIndex: 'uploadTime', width: 170 },
        {
            title: '操作',
            key: 'action',
            width: 200,
            fixed: 'right',
            render: (_, record) => (
                // 平台角色可代企业操作（数据范围为全平台）
                <Space>
                    {record.status === 'uploadFailed' && (
                        <Button type="link" size="small" icon={<UploadOutlined />} onClick={() => { setCurrentRecord(record); setUploadOpen(true); }}>再次上传</Button>
                    )}
                    {record.status === 'available' && (
                        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => { setCurrentRecord(record); setEditOpen(true); }}>编辑</Button>
                    )}
                    {['uploadFailed', 'error'].includes(record.status) && (
                        <Popconfirm title="确定删除该编译链？" onConfirm={() => handleDelete(record)}>
                            <Button type="link" size="small" danger>删除</Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0 }}>编译链管理</Title>
                <CompileRoleSwitcher role={role} onChange={handleRoleChange} />
            </div>

            {/* 筛选区 */}
            <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Space wrap>
                        {isPlatform && (
                            <Select
                                placeholder="所属企业"
                                value={searchEnterprise}
                                onChange={setSearchEnterprise}
                                style={{ width: 200 }}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                options={MOCK_ENTERPRISES.map(e => ({ value: e, label: e }))}
                            />
                        )}
                        <Space.Compact>
                            <Select value={searchType} onChange={setSearchType} style={{ width: 100 }} options={[{ value: 'name', label: '编译链' }, { value: 'id', label: 'ID' }]} />
                            <Input
                                placeholder="搜索编译链"
                                value={searchName}
                                onChange={(e) => setSearchName(e.target.value)}
                                style={{ width: 200 }}
                                allowClear
                            />
                        </Space.Compact>
                        <Select
                            placeholder="状态"
                            value={searchStatus}
                            onChange={setSearchStatus}
                            style={{ width: 120 }}
                            allowClear
                            options={Object.entries(STATUS_MAP).map(([k, v]) => ({ value: k, label: v.text }))}
                        />
                        <Button type="primary" icon={<SearchOutlined />}>查询</Button>
                        <Button icon={<ReloadOutlined />} onClick={() => { setSearchName(''); setSearchStatus(undefined); setSearchEnterprise(undefined); }}>重置</Button>
                    </Space>
                    <Button type="primary" icon={<UploadOutlined />} onClick={() => { setCurrentRecord(null); setUploadOpen(true); }}>上传编译链</Button>
                </div>
            </Card>

            {/* 表格 */}
            <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Table
                    dataSource={dataSource}
                    columns={columns}
                    rowKey="id"
                    scroll={{ x: isPlatform ? 1500 : 1320 }}
                    pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
                />
            </Card>

            <UploadModal open={uploadOpen} isPlatform={isPlatform} record={currentRecord} onClose={() => setUploadOpen(false)} />
            <DetailDrawer open={detailOpen} record={currentRecord} onClose={() => setDetailOpen(false)} />
            <EditModal open={editOpen} record={currentRecord} onClose={() => setEditOpen(false)} />
        </div>
    );
};

export default ToolchainManage;
